import { useState, useEffect } from 'react';
import { Car, Search, Filter, ArrowRight, Loader2 } from 'lucide-react';
import { NavigationBar } from "./shared/Navigation";
import { Footer } from "./shared/Footer";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Slider } from "./ui/slider";
import { Badge } from "./ui/badge";
import { useLanguage } from '../contexts/LanguageContext';
import { apiService, CarListing as ApiCarListing } from '../services/api';
import { NoCarsFoundModal } from './NoCarsFoundModal';

export interface Car {
  id: number;
  make: string;
  model: string;
  year: number;
  monthlyPayment: number;
  termMonths: number;
  minCreditScore: number;
  type: string;
  dealer: string;
  mileage?: number;
  transmission?: string;
  fuelType?: string;
  vin?: string;
  totalPrice?: number;
  downPayment?: number;
  description?: string;
  additionalRequirements?: string;
  images?: string[];
  location: {
    country: string;
    stateProvince: string;
    city: string;
  };
}

export const mockCars: Car[] = [
  {
    id: 1,
    make: "Toyota",
    model: "Camry",
    year: 2023,
    monthlyPayment: 385,
    termMonths: 72,
    minCreditScore: 600,
    type: "Sedan",
    dealer: "Elite Motors",
    mileage: 25000,
    transmission: "Automatic",
    fuelType: "Gasoline",
    totalPrice: 27720,
    location: {
      country: "USA",
      stateProvince: "Minnesota",
      city: "Minneapolis"
    }
  },
  {
    id: 2,
    make: "Honda",
    model: "Accord",
    year: 2024,
    monthlyPayment: 425,
    termMonths: 60,
    minCreditScore: 650,
    type: "Sedan",
    dealer: "Premium Auto Sales",
    mileage: 15000,
    transmission: "Automatic",
    fuelType: "Gasoline",
    totalPrice: 25500,
    location: {
      country: "USA",
      stateProvince: "Texas",
      city: "Austin"
    }
  },
  {
    id: 3,
    make: "Nissan",
    model: "Altima",
    year: 2023,
    monthlyPayment: 350,
    termMonths: 66,
    minCreditScore: 580,
    type: "Sedan",
    dealer: "Metro Car Center",
    mileage: 30000,
    transmission: "CVT",
    fuelType: "Gasoline",
    totalPrice: 23100,
    location: {
      country: "Canada",
      stateProvince: "Ontario",
      city: "Toronto"
    }
  },
  {
    id: 4,
    make: "Mazda",
    model: "CX-5",
    year: 2024,
    monthlyPayment: 465,
    termMonths: 60,
    minCreditScore: 620,
    type: "SUV",
    dealer: "City Auto Group",
    mileage: 12000,
    transmission: "Automatic",
    fuelType: "Gasoline",
    totalPrice: 27900,
    location: {
      country: "Canada",
      stateProvince: "Quebec",
      city: "Montreal"
    }
  },
  {
    id: 5,
    make: "Hyundai",
    model: "Elantra",
    year: 2023,
    monthlyPayment: 295,
    termMonths: 72,
    minCreditScore: 550,
    type: "Sedan",
    dealer: "Value Auto Sales",
    mileage: 35000,
    transmission: "Automatic",
    fuelType: "Gasoline",
    totalPrice: 21240,
    location: {
      country: "USA",
      stateProvince: "Minnesota",
      city: "St. Paul"
    }
  },
  {
    id: 6,
    make: "Subaru",
    model: "Outback",
    year: 2024,
    monthlyPayment: 485,
    termMonths: 60,
    minCreditScore: 640,
    type: "SUV",
    dealer: "Mountain View Motors",
    mileage: 8000,
    transmission: "CVT",
    fuelType: "Gasoline",
    totalPrice: 29100,
    location: {
      country: "USA",
      stateProvince: "Texas",
      city: "Houston"
    }
  }
];

// Location data constants
const locationData = {
  countries: ['USA', 'Canada'],
  states: {
    USA: ['Minnesota', 'Texas'],
    Canada: ['Ontario', 'Quebec']
  },
  cities: {
    'USA-Minnesota': ['Minneapolis', 'St. Paul'],
    'USA-Texas': ['Austin', 'Houston'],
    'Canada-Ontario': ['Toronto', 'Ottawa'],
    'Canada-Quebec': ['Montreal', 'Quebec City']
  }
};

function FiltersSection({ 
  filters, 
  onFiltersChange,
  cars
}: { 
  filters: any; 
  onFiltersChange: (filters: any) => void;
  cars: Car[];
}) {
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const { t } = useLanguage();

  const FilterContent = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('ui.search')}
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder={t('listings.filters.search')}
            value={filters.search}
            onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
            className="pl-10"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('listings.filters.make')}
        </label>
        <Select value={filters.make} onValueChange={(value) => onFiltersChange({ ...filters, make: value })}>
          <SelectTrigger>
            <SelectValue placeholder={t('listings.filters.make')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('listings.filters.allMakes')}</SelectItem>
            <SelectItem value="Toyota">Toyota</SelectItem>
            <SelectItem value="Honda">Honda</SelectItem>
            <SelectItem value="Nissan">Nissan</SelectItem>
            <SelectItem value="Mazda">Mazda</SelectItem>
            <SelectItem value="Hyundai">Hyundai</SelectItem>
            <SelectItem value="Subaru">Subaru</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('listings.filters.type')}
        </label>
        <Select value={filters.type} onValueChange={(value) => onFiltersChange({ ...filters, type: value })}>
          <SelectTrigger>
            <SelectValue placeholder={t('listings.filters.allTypes')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('listings.filters.allTypes')}</SelectItem>
            <SelectItem value="Sedan">{t('vehicleType.sedan')}</SelectItem>
            <SelectItem value="SUV">{t('vehicleType.suv')}</SelectItem>
            <SelectItem value="Hatchback">{t('vehicleType.hatchback')}</SelectItem>
            <SelectItem value="Truck">{t('vehicleType.truck')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('listings.filters.monthlyPayment')}: ${filters.monthlyPayment[0]} - ${filters.monthlyPayment[1]}
        </label>
        <Slider
          value={filters.monthlyPayment}
          onValueChange={(value) => onFiltersChange({ ...filters, monthlyPayment: value })}
          max={cars.length > 0 ? Math.ceil(Math.max(...cars.map(car => car.monthlyPayment)) * 1.1) : 1200}
          min={0}
          step={25}
          className="w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('listings.filters.creditScore')}: {filters.creditScore}
        </label>
        <Slider
          value={[filters.creditScore]}
          onValueChange={(value) => onFiltersChange({ ...filters, creditScore: value[0] })}
          max={850}
          min={300}
          step={10}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{t('listings.filters.poor')}</span>
          <span>{t('listings.filters.excellent')}</span>
        </div>
        <p className="text-xs text-gray-600 mt-2">
          {t('listings.filters.creditHelp')}
        </p>
      </div>

      {/* Location Filters */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('listings.filters.country')}
        </label>
        <Select value={filters.country} onValueChange={(value) => {
          onFiltersChange({ 
            ...filters, 
            country: value, 
            stateProvince: 'all', 
            city: 'all' 
          });
        }}>
          <SelectTrigger>
            <SelectValue placeholder={t('listings.filters.allCountries')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('listings.filters.allCountries')}</SelectItem>
            {locationData.countries.map(country => (
              <SelectItem key={country} value={country}>{country}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {filters.country === 'Canada' ? t('listings.filters.province') : t('listings.filters.state')}
        </label>
        <Select 
          value={filters.stateProvince} 
          onValueChange={(value) => {
            onFiltersChange({ 
              ...filters, 
              stateProvince: value, 
              city: 'all' 
            });
          }}
          disabled={filters.country === 'all'}
        >
          <SelectTrigger>
            <SelectValue placeholder={filters.country === 'Canada' ? t('listings.filters.allProvinces') : t('listings.filters.allStates')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {filters.country === 'Canada' ? t('listings.filters.allProvinces') : t('listings.filters.allStates')}
            </SelectItem>
            {filters.country !== 'all' && locationData.states[filters.country as keyof typeof locationData.states]?.map(state => (
              <SelectItem key={state} value={state}>{state}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('listings.filters.city')}
        </label>
        <Select 
          value={filters.city} 
          onValueChange={(value) => onFiltersChange({ ...filters, city: value })}
          disabled={filters.country === 'all' || filters.stateProvince === 'all'}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('listings.filters.allCities')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('listings.filters.allCities')}</SelectItem>
            {filters.country !== 'all' && filters.stateProvince !== 'all' && 
              locationData.cities[`${filters.country}-${filters.stateProvince}` as keyof typeof locationData.cities]?.map(city => (
                <SelectItem key={city} value={city}>{city}</SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      <Button 
        variant="outline" 
        onClick={() => {
          const maxMonthlyPayment = cars.length > 0 ? Math.max(...cars.map(car => car.monthlyPayment)) : 1200;
          const dynamicMax = Math.ceil(maxMonthlyPayment * 1.1);
          
          onFiltersChange({
            search: '',
            make: 'all',
            type: 'all',
            monthlyPayment: [0, dynamicMax] as [number, number],
            creditScore: 650,
            country: 'all',
            stateProvince: 'all',
            city: 'all'
          });
        }}
        className="w-full"
      >
        {t('listings.filters.clearAll')}
      </Button>
    </div>
  );

  return (
    <>
      <div className="hidden lg:block">
        <Card className="border-[#f91bdc]/20 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2 mb-6">
              <Filter className="text-[#f91bdc]" size={20} />
              <h2 className="text-lg font-semibold text-gray-900">{t('listings.filters.title')}</h2>
            </div>
            <FilterContent />
          </CardContent>
        </Card>
      </div>

      <div className="lg:hidden mb-6">
        <Button 
          onClick={() => setShowMobileFilters(true)}
          className="w-full bg-[#f91bdc] hover:bg-[#e015c9]"
        >
          <Filter size={20} className="mr-2" />
          {t('listings.filters.showFilters')}
        </Button>
      </div>

      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowMobileFilters(false)} />
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">{t('listings.filters.title')}</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowMobileFilters(false)}>
                <ArrowRight size={20} />
              </Button>
            </div>
            <FilterContent />
          </div>
        </div>
      )}
    </>
  );
}

function CarCard({ 
  car, 
  onCarSelect, 
  onCarBuy, 
  userCreditScore 
}: { 
  car: Car; 
  onCarSelect: (car: Car) => void; 
  onCarBuy: (car: Car) => void;
  userCreditScore: number;
}) {
  const isEligible = userCreditScore >= car.minCreditScore;
  const { t } = useLanguage();

  return (
    <Card 
      className={`group cursor-pointer hover:shadow-xl transition-all duration-300 overflow-hidden h-[480px] ${
        !isEligible ? 'opacity-60' : ''
      }`}
      onClick={() => onCarSelect(car)}
    >
      {/* Car image section - 55% of card height, positioned on top */}
      <div className="relative h-[55%] overflow-hidden bg-gray-100">
        {car.images && car.images.length > 0 ? (
          <div className="relative w-full h-full">
            <img
              src={car.images[0]}
              alt={`${car.year} ${car.make} ${car.model}`}
              className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
              onError={(e) => {
                // Fallback to placeholder on error
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const placeholder = target.nextElementSibling as HTMLDivElement;
                if (placeholder) placeholder.style.display = 'flex';
              }}
            />
            {/* Error fallback placeholder (hidden by default) */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#f91bdc]/10 to-[#f91bdc]/20 flex items-center justify-center" style={{ display: 'none' }}>
              <Car size={80} className="text-[#f91bdc]/60" />
            </div>
          </div>
        ) : (
          <div className="h-full bg-gradient-to-br from-[#f91bdc]/10 to-[#f91bdc]/20 flex items-center justify-center">
            <Car size={80} className="text-[#f91bdc]/60" />
          </div>
        )}
        
        <div className="absolute top-4 left-4">
          {!isEligible && (
            <Badge variant="destructive" className="mb-2">
              {t('car.creditRequirementNotMet')}
            </Badge>
          )}
          {car.mileage && car.mileage < 20000 && (
            <Badge className="bg-green-500 hover:bg-green-600">
              {t('car.lowMileage')}
            </Badge>
          )}
        </div>

        <div className="absolute top-4 right-4">
          <Badge className="bg-[#f91bdc] hover:bg-[#e015c9] text-white text-sm">
            ${car.monthlyPayment}/mo
          </Badge>
        </div>
      </div>

      {/* Card content - 45% of card height */}
      <CardContent className="h-[45%] p-5 flex flex-col justify-between">
        <div className="flex-1">
          <div className="mb-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 leading-tight">
              {car.year} {car.make} {car.model}
            </h3>
            <p className="text-sm text-gray-600">{car.type} • {car.dealer}</p>
            <p className="text-xs text-gray-500">{car.location.city}, {car.location.stateProvince}, {car.location.country}</p>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
            <span>{t('car.creditScore')}: {car.minCreditScore}</span>
            {car.mileage && <span>{car.mileage.toLocaleString()} {t('car.mileage')}</span>}
          </div>

          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xl font-bold text-[#f91bdc]">
                ${car.monthlyPayment}/mo
              </div>
              <p className="text-xs text-gray-500">{car.termMonths} {t('car.months')}</p>
            </div>
            {car.totalPrice && (
              <div className="text-right">
                <p className="text-xs text-gray-500">{t('car.totalPrice')}</p>
                <p className="text-sm font-semibold">${car.totalPrice.toLocaleString()}</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex space-x-2 mt-auto">
          <Button 
            variant="outline" 
            size="sm"
            className="flex-1 text-xs py-2"
            onClick={(e) => {
              e.stopPropagation();
              onCarSelect(car);
            }}
          >
            {t('common.viewDetails')}
          </Button>
          <Button 
            size="sm"
            className={`flex-1 text-xs py-2 ${isEligible ? 'bg-[#f91bdc] hover:bg-[#e015c9]' : 'bg-gray-400 cursor-not-allowed'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (isEligible) onCarBuy(car);
            }}
            disabled={!isEligible}
          >
            {t('common.buyNow')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Listings({
  onCarSelect,
  onCarBuy,
  onContactClick,
  onOpenTerms,
  onOpenPrivacy
}: {
  onNavigate: (page: string) => void;
  onCarSelect: (car: Car) => void;
  onCarBuy: (car: Car) => void;
  onContactClick: () => void;
  onOpenTerms: () => void;
  onOpenPrivacy: () => void;
}) {
  const { t } = useLanguage();
  const [filters, setFilters] = useState({
    search: '',
    make: 'all',
    type: 'all',
    monthlyPayment: [0, 1200] as [number, number], // Will be updated dynamically
    creditScore: 650,
    country: 'all',
    stateProvince: 'all',
    city: 'all'
  });

  const [sortBy, setSortBy] = useState('payment-low');
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNoCarsModal, setShowNoCarsModal] = useState(false);

  useEffect(() => {
    const fetchCars = async () => {
      setLoading(true);
      try {
        const response = await apiService.getCarListings({
          limit: 50, // Request more cars to show all available listings
          make: filters.make !== 'all' ? filters.make : undefined,
          // Don't filter by price on the backend, let frontend handle it
        });
        
        // Transform API car listings to our Car interface (use listing location or fall back to dealer city/state)
        const transformedCars: Car[] = (response.data || []).map((apiCar: ApiCarListing) => {
          const parts = apiCar.location?.split(', ').map((p: string) => p.trim()) || [];
          const city = parts[0] || apiCar.dealer_city || 'Unknown';
          const stateProvince = parts[1] || apiCar.dealer_state || 'Unknown';
          const country = parts[2] || (stateProvince === 'Quebec' || stateProvince === 'Ontario' ? 'Canada' : 'USA');
          return {
            id: apiCar.id,
            make: apiCar.make,
            model: apiCar.model,
            year: apiCar.year,
            monthlyPayment: apiCar.monthly_payment ?? Math.round(apiCar.price / 60),
            termMonths: apiCar.term_months ?? 60,
            minCreditScore: apiCar.min_credit_score ?? 650,
            type: apiCar.vehicle_type || 'Sedan',
            dealer: apiCar.dealer_name || 'Unknown Dealer',
            mileage: apiCar.mileage,
            transmission: apiCar.transmission,
            fuelType: apiCar.fuel_type,
            vin: apiCar.vin,
            totalPrice: apiCar.price,
            downPayment: apiCar.down_payment,
            description: apiCar.description,
            additionalRequirements: Array.isArray(apiCar.features) ? apiCar.features.join(' • ') : undefined,
            images: apiCar.images || [],
            location: { country, stateProvince, city }
          };
        });
        
        setCars(transformedCars);
        
        // Update price range dynamically based on available cars
        if (transformedCars.length > 0) {
          const maxMonthlyPayment = Math.max(...transformedCars.map(car => car.monthlyPayment));
          const dynamicMax = Math.ceil(maxMonthlyPayment * 1.1); // Add 10% to max
          
          setFilters(prev => ({
            ...prev,
            monthlyPayment: [0, dynamicMax] as [number, number]
          }));
        }
      } catch (err) {
        setError('Failed to fetch car listings.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCars();
  }, [filters.make]); // Remove monthlyPayment from dependencies to avoid infinite loop

  // Show modal when page loads and there are no cars
  useEffect(() => {
    if (!loading && cars.length === 0 && !error) {
      // Small delay to ensure page is fully rendered
      const timer = setTimeout(() => {
        setShowNoCarsModal(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, cars.length, error]);

  const filteredCars = cars
    .filter(car => {
      if (filters.search && !`${car.make} ${car.model}`.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }
      if (filters.make !== 'all' && car.make !== filters.make) {
        return false;
      }
      if (filters.type !== 'all' && car.type !== filters.type) {
        return false;
      }
      if (car.monthlyPayment < filters.monthlyPayment[0] || car.monthlyPayment > filters.monthlyPayment[1]) {
        return false;
      }
      if (filters.country !== 'all' && car.location.country !== filters.country) {
        return false;
      }
      if (filters.stateProvince !== 'all' && car.location.stateProvince !== filters.stateProvince) {
        return false;
      }
      if (filters.city !== 'all' && car.location.city !== filters.city) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'payment-low':
          return a.monthlyPayment - b.monthlyPayment;
        case 'payment-high':
          return b.monthlyPayment - a.monthlyPayment;
        case 'year-new':
          return b.year - a.year;
        case 'year-old':
          return a.year - b.year;
        default:
          return 0;
      }
    });

  const eligibleCars = filteredCars.filter(car => filters.creditScore >= car.minCreditScore);

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar 
        onContactClick={onContactClick} 
      />
      
      <section className="bg-gradient-to-br from-[#f91bdc]/10 via-white to-[#f91bdc]/5 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
                dangerouslySetInnerHTML={{ __html: t('listings.title') }}>
            </h1>
            <p className="text-xl text-gray-600 mb-6 max-w-2xl mx-auto">
              {t('listings.subtitle')}
            </p>
            <div className="flex items-center justify-center space-x-4 text-gray-500">
              <Badge variant="outline" className="border-[#f91bdc] text-[#f91bdc]">
                {cars.length} {t('listings.carsAvailable')}
              </Badge>
              <Badge variant="outline" className="border-[#f91bdc] text-[#f91bdc]">
                {eligibleCars.length} {t('listings.youQualifyFor')}
              </Badge>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-4 lg:gap-8">
          <div className="lg:col-span-1">
            <FiltersSection filters={filters} onFiltersChange={setFilters} cars={cars} />
          </div>

          <div className="lg:col-span-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {filteredCars.length} {t('listings.carsFound')}
                </h2>
                <p className="text-gray-600">
                  {eligibleCars.length} {t('listings.availableForCredit')} {filters.creditScore}
                </p>
              </div>
              
              <div className="mt-4 sm:mt-0">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={t('listings.sortBy')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payment-low">{t('listings.sort.paymentLow')}</SelectItem>
                    <SelectItem value="payment-high">{t('listings.sort.paymentHigh')}</SelectItem>
                    <SelectItem value="year-new">{t('listings.sort.yearNew')}</SelectItem>
                    <SelectItem value="year-old">{t('listings.sort.yearOld')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-12 w-12 text-gray-500 animate-spin" />
                <p className="ml-4 text-gray-500">{t('listings.loadingCars')}</p>
              </div>
            ) : error ? (
              <Card className="p-12 text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('listings.error.title')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('listings.error.message')}
                </p>
                <Button 
                  onClick={() => {
                    const maxMonthlyPayment = cars.length > 0 ? Math.max(...cars.map(car => car.monthlyPayment)) : 1200;
                    const dynamicMax = Math.ceil(maxMonthlyPayment * 1.1);
                    setFilters({
                      search: '',
                      make: 'all',
                      type: 'all',
                      monthlyPayment: [0, dynamicMax] as [number, number],
                      creditScore: 650,
                      country: 'all',
                      stateProvince: 'all',
                      city: 'all'
                    });
                  }}
                  className="bg-[#f91bdc] hover:bg-[#e015c9]"
                >
                  {t('listings.error.clearFilters')}
                </Button>
              </Card>
            ) : filteredCars.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {filteredCars.map(car => (
                  <CarCard
                    key={car.id}
                    car={car}
                    onCarSelect={onCarSelect}
                    onCarBuy={onCarBuy}
                    userCreditScore={filters.creditScore}
                  />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <Car size={64} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('listings.noCars.title')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('listings.noCars.message')}
                </p>
                <Button 
                  onClick={() => {
                    const maxMonthlyPayment = cars.length > 0 ? Math.max(...cars.map(car => car.monthlyPayment)) : 1200;
                    const dynamicMax = Math.ceil(maxMonthlyPayment * 1.1);
                    setFilters({
                      search: '',
                      make: 'all',
                      type: 'all',
                      monthlyPayment: [0, dynamicMax] as [number, number],
                      creditScore: 650,
                      country: 'all',
                      stateProvince: 'all',
                      city: 'all'
                    });
                  }}
                  className="bg-[#f91bdc] hover:bg-[#e015c9]"
                >
                  {t('listings.noCars.clearFilters')}
                </Button>
              </Card>
            )}
          </div>
        </div>
      </div>

      <Footer 
        onOpenTerms={onOpenTerms} 
        onOpenPrivacy={onOpenPrivacy} 
      />

      <NoCarsFoundModal 
        isOpen={showNoCarsModal}
        onClose={() => setShowNoCarsModal(false)}
      />
    </div>
  );
}